import { CustomerPermissionCodes, UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import GrantJournal from "@pages/reports/grantJournal/GrantJournal";
import VatOssStatementReport from "@pages/reports/vatOssStatementReport/VatOssStatementReport";
import React, { lazy } from "react";

import {
    ROUTE_ACCOUNT_ANALYSIS,
    ROUTE_ACCOUNTING_JOURNAL,
    ROUTE_AGING_AP,
    ROUTE_AGING_AR,
    ROUTE_ASSET_ANALYSIS,
    ROUTE_BALANCE_SHEET,
    ROUTE_CBA_ENTRY_LEDGER,
    ROUTE_CBA_EXPENSES_AND_REVENUE,
    ROUTE_DOCUMENT_JOURNAL,
    ROUTE_EXCHANGE_RATES,
    ROUTE_EXPENSES_AND_REVENUE,
    ROUTE_GENERAL_LEDGER,
    ROUTE_GRANT_JOURNAL,
    ROUTE_INCOME_AND_EXPENDITURE,
    ROUTE_INCOME_STATEMENT,
    ROUTE_PAYMENT_JOURNAL,
    ROUTE_RECEIVABLE_ADJUSTMENTS,
    ROUTE_SALDO,
    ROUTE_VAT_CONTROL_STATEMENT_REPORT,
    ROUTE_VAT_OSS_STATEMENT_REPORT,
    ROUTE_VAT_STATEMENT_REPORT,
    ROUTE_VAT_VIES_STATEMENT_REPORT
} from "../routes";
import CompanyDependentPrivateRoute from "../routes/CompanyDependentPrivateRoute";
import FormRoute from "../routes/FormRoute";
import PrivateRoute from "../routes/PrivateRoute";

const VatControlStatementReport = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/vatControlStatementReport/VatControlStatementReport"));
const VatVIESStatementReport = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/vatVIESStatementReport/VatVIESStatementReport"));
const VatStatementReport = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/vatStatementReport/VatStatementReport"));
const GeneralLedger = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/generalLedger/GeneralLedger"));
const AccountingJournal = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/accountingJournal/AccountingJournal"));
const AccountAnalysis = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/accountAnalysis/AccountAnalysis"));
const AssetAnalysis = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/assetAnalysis/AssetAnalysis"));
const CbaIncomeExpense = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/cbaIncomeExpense/cbaIncomeExpense"));
const CbaEntryLedger = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/cbaEntryLedger/CbaEntryLedger"));
const CbaExpensesAndRevenue = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/cbaExpensesAndRevenue/CbaExpensesAndRevenue"));
const DocumentJournal = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/documentJournal/DocumentJournal"));
const ExpensesAndRevenue = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/expensesAndRevenue/ExpensesAndRevenue"));
const PaymentJournal = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/paymentJournal/PaymentJournal"));
const AgingAP = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/aging/AgingAP"));
const AgingAR = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/aging/AgingAR"));
const BalanceSheet = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/balanceSheet/BalanceSheet"));
const IncomeStatement = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/incomeStatement/IncomeStatement"));
const ExchangeRates = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/exchangeRates/ExchangeRate"));
const ReceivableAdjustments = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/receivableAdjustments/ReceivableAdjustments"));
const Saldo = lazy(() => import(/* webpackChunkName: "reports" */"../pages/reports/saldo/Saldo"));

export default function reportsBundle(): React.ReactElement[] {
    return [
        <PrivateRoute
            path={ROUTE_CBA_EXPENSES_AND_REVENUE}
            key={ROUTE_CBA_EXPENSES_AND_REVENUE}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={CbaExpensesAndRevenue}/>,
        <PrivateRoute
            path={ROUTE_EXPENSES_AND_REVENUE}
            key={ROUTE_EXPENSES_AND_REVENUE}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={ExpensesAndRevenue}/>,
        <PrivateRoute
            path={ROUTE_GENERAL_LEDGER}
            key={ROUTE_GENERAL_LEDGER}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={GeneralLedger}/>,
        <PrivateRoute
            path={ROUTE_ACCOUNTING_JOURNAL}
            key={ROUTE_ACCOUNTING_JOURNAL}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={AccountingJournal}/>,
        <PrivateRoute
            path={ROUTE_ACCOUNT_ANALYSIS}
            key={ROUTE_ACCOUNT_ANALYSIS}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={AccountAnalysis}/>,
        <PrivateRoute
            path={ROUTE_ASSET_ANALYSIS}
            key={ROUTE_ASSET_ANALYSIS}
            CPs={[CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.Reports]}
            component={AssetAnalysis}/>,
        <PrivateRoute
            path={ROUTE_INCOME_AND_EXPENDITURE}
            key={ROUTE_INCOME_AND_EXPENDITURE}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={CbaIncomeExpense}/>,
        <PrivateRoute
            path={ROUTE_CBA_ENTRY_LEDGER}
            key={ROUTE_CBA_ENTRY_LEDGER}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={CbaEntryLedger}/>,
        <PrivateRoute
            path={ROUTE_DOCUMENT_JOURNAL}
            key={ROUTE_DOCUMENT_JOURNAL}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={DocumentJournal}/>,
        <PrivateRoute
            path={ROUTE_PAYMENT_JOURNAL}
            key={ROUTE_PAYMENT_JOURNAL}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={PaymentJournal}/>,
        <PrivateRoute
            path={ROUTE_AGING_AP}
            key={ROUTE_AGING_AP}
            CPs={[CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.Reports]}
            component={AgingAP}/>,
        <PrivateRoute
            path={ROUTE_AGING_AR}
            key={ROUTE_AGING_AR}
            CPs={[CustomerPermissionCodes.CompanyInfo, UserPermissionCodes.Reports]}
            component={AgingAR}/>,
        <PrivateRoute
            path={ROUTE_BALANCE_SHEET}
            key={ROUTE_BALANCE_SHEET}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={BalanceSheet}/>,
        <PrivateRoute
            path={ROUTE_INCOME_STATEMENT}
            key={ROUTE_INCOME_STATEMENT}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={IncomeStatement}/>,
        <FormRoute
            path={ROUTE_EXCHANGE_RATES}
            key={ROUTE_EXCHANGE_RATES}
            entityType={EntityTypeName.ExchangeRate}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            page={ExchangeRates}/>,
        <PrivateRoute
            path={ROUTE_GRANT_JOURNAL}
            key={ROUTE_GRANT_JOURNAL}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={GrantJournal}/>,
        <PrivateRoute
            path={ROUTE_VAT_CONTROL_STATEMENT_REPORT}
            key={ROUTE_VAT_CONTROL_STATEMENT_REPORT}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={VatControlStatementReport}/>,
        <PrivateRoute
            path={ROUTE_VAT_VIES_STATEMENT_REPORT}
            key={ROUTE_VAT_VIES_STATEMENT_REPORT}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={VatVIESStatementReport}/>,
        <PrivateRoute
            path={ROUTE_VAT_STATEMENT_REPORT}
            key={ROUTE_VAT_STATEMENT_REPORT}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={VatStatementReport}/>,
        <PrivateRoute
            path={ROUTE_VAT_OSS_STATEMENT_REPORT}
            key={ROUTE_VAT_OSS_STATEMENT_REPORT}
            CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
            component={VatOssStatementReport}/>,
        <PrivateRoute path={ROUTE_SALDO}
                      key={ROUTE_SALDO}
                      CPs={[UserPermissionCodes.Reports, CustomerPermissionCodes.AccountingReports]}
                      component={Saldo}/>,
        <CompanyDependentPrivateRoute
            path={ROUTE_RECEIVABLE_ADJUSTMENTS}
            key={ROUTE_RECEIVABLE_ADJUSTMENTS}
            CPs={UserPermissionCodes.ReceivableAdjustments}
            component={ReceivableAdjustments}/>
    ];
}